import { LoadingShim } from "@narmi/design_system";
import { type QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { type ReactNode, Suspense } from "react";
import ToastsContainer from "@lending/components/ToastsContainer/ToastsContainer";
import { ToastsContextProvider } from "@lending/hooks/useToastsProvider";
import { defaultQueryClient } from "@lending/lib/queryConfig";
import { type LendingAuthMode } from "./contexts/LendingHostContext";
import { LendingHostProvider } from "./contexts/LendingHostProvider";
import { LocalizationContent } from "./contexts/LocalizationContext";
import { StoreProvider } from "./contexts/StoreProvider";

interface LendingProvidersProps {
  /**
   * Selects the credential family the lending API client authenticates with.
   * Today this module supports only `lending_session`; hosts should omit this
   * unless/until additional auth modes are introduced.
   */
  authMode?: LendingAuthMode;
  /**
   * Allow a host (e.g. azul) to supply its own QueryClient. Standalone lending
   * uses the module default.
   */
  queryClient?: QueryClient;
  /** Host-owned logout handler; see `LendingHostContextValue.onLogout`. */
  onLogout?: () => void;
  children: ReactNode;
}

/**
 * Router-free provider shell for the lending module.
 *
 * This intentionally owns NO router creation, app chrome (favicon/theming), or
 * auth redirects — those are host concerns. It provides only the cross-cutting
 * context every lending page needs: React Query, host/auth mode, localization,
 * toasts, and the flow store.
 */
export function LendingProviders({
  authMode = "lending_session",
  queryClient = defaultQueryClient,
  onLogout,
  children,
}: LendingProvidersProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <LendingHostProvider authMode={authMode} onLogout={onLogout}>
        <LocalizationContent>
          <ToastsContextProvider>
            <ToastsContainer />
            <Suspense
              fallback={
                <LoadingShim isLoading>
                  <div style={{ height: "100vh" }} />
                </LoadingShim>
              }
            >
              <StoreProvider>{children}</StoreProvider>
            </Suspense>
          </ToastsContextProvider>
        </LocalizationContent>
      </LendingHostProvider>
    </QueryClientProvider>
  );
}
